import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ShieldCheck, Trash2, CheckCircle, Plus, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useAuth } from '../components/AuthGuard';

const InputBlock = ({ label, type = 'text', value, onChange, placeholder, required = false }) => (
    <div>
        <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            required={required}
            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium placeholder-zinc-600"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
        />
    </div>
);

const AccessManager = () => {
    const { user, role: currentUserRole } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(true);

    // Form State
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [role, setRole] = useState('viewer');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'system_roles'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));
            
            // Sort by role precedence, then email
            const roleOrder = { 'ecosystem_director': 1, 'manager': 2, 'viewer': 3, 'artist': 4, 'crew': 5 };
            data.sort((a, b) => {
                const orderA = roleOrder[a.role] || 99;
                const orderB = roleOrder[b.role] || 99;
                if (orderA !== orderB) return orderA - orderB;
                return a.id.localeCompare(b.id);
            });
            
            setUsers(data);
        } catch (e) {
            console.error('Error fetching system roles:', e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!email.trim() || !role) return;
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address.');
            return;
        }

        setStatusMessage('saving');
        const emailId = email.trim().toLowerCase();
        
        try {
            await setDoc(doc(db, 'system_roles', emailId), {
                role,
                displayName: displayName.trim() || emailId.split('@')[0],
                addedAt: new Date().toISOString(),
                assignedVia: 'AdminConsole',
                status: 'active',
                addedBy: user.email
            });
            
            setEmail('');
            setDisplayName('');
            setRole('viewer');
            setStatusMessage('success');
            fetchUsers();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error('Error adding user:', e);
            setStatusMessage('error');
            alert('Failed to add user. Ensure you have ecosystem_director privileges.');
        }
    };

    const handleDelete = async (id, userRole) => {
        if (userRole === 'ecosystem_director') {
            alert('Cannot delete an ecosystem director from this console.');
            return;
        }
        if (id === user.email) {
            alert('You cannot delete your own account.');
            return;
        }
        if (!window.confirm(`Are you sure you want to REVOKE access for ${id}? They will be immediately locked out of the system.`)) return;
        
        try {
            await deleteDoc(doc(db, 'system_roles', id));
            fetchUsers();
        } catch (e) {
            console.error(e);
            alert('Failed to delete user.');
        }
    };

    const selectCls = 'w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-3 focus:outline-none font-bold';

    const getRoleBadgeColor = (r) => {
        switch (r) {
            case 'ecosystem_director': return 'bg-purple-900/50 text-purple-400 border-purple-700/50';
            case 'manager': return 'bg-emerald-900/50 text-emerald-400 border-emerald-700/50';
            case 'admin': return 'bg-red-900/50 text-red-400 border-red-700/50';
            case 'trainer': return 'bg-blue-900/50 text-blue-400 border-blue-700/50';
            case 'student': return 'bg-amber-900/50 text-amber-400 border-amber-700/50';
            case 'sponsor': return 'bg-cyan-900/50 text-cyan-400 border-cyan-700/50';
            case 'parent': return 'bg-fuchsia-900/50 text-fuchsia-400 border-fuchsia-700/50';
            case 'viewer': return 'bg-zinc-800 text-zinc-400 border-zinc-700';
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-black tracking-tight text-white flex items-center gap-3">
                    <ShieldCheck size={28} className="text-[#8B1A2D]" /> ACCESS CONTROL
                </h1>
                <p className="text-zinc-400 text-sm font-bold uppercase tracking-widest mt-1">Manage System Whitelist &amp; Role Permissions</p>
            </div>

            {/* Add User Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-sm">
                <div className="flex justify-between items-center cursor-pointer mb-6" onClick={() => setIsFormOpen(!isFormOpen)}>
                    <h2 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] flex items-center gap-2">
                        <Plus size={14} /> WHITELIST NEW USER
                    </h2>
                    {isFormOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                </div>

                {isFormOpen && (
                    <form onSubmit={handleAdd} className="space-y-4">
                        <div className="p-4 bg-zinc-800/60 rounded-xl border border-zinc-700 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <InputBlock 
                                label="Google Email Address" 
                                type="email" 
                                value={email} 
                                onChange={e => setEmail(e.target.value)} 
                                placeholder="employee@gmail.com" 
                                required 
                            />
                            <InputBlock 
                                label="Display Name (Optional)" 
                                value={displayName} 
                                onChange={e => setDisplayName(e.target.value)} 
                                placeholder="Jane Doe" 
                            />
                            <div>
                                <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">System Role *</label>
                                <select value={role} onChange={e => setRole(e.target.value)} className={selectCls} required>
                                    <optgroup label="Nexus (Internal System)">
                                        <option value="manager">Manager (Full Access, No Config)</option>
                                        <option value="viewer">Viewer (Read-Only Access)</option>
                                    </optgroup>
                                    <optgroup label="DIS Portal (Public Access)">
                                        <option value="admin">Admin (Portal Coordinator)</option>
                                        <option value="trainer">Trainer (Course Management)</option>
                                        <option value="student">Student (Course Files)</option>
                                        <option value="sponsor">Sponsor (Financial Overviews)</option>
                                        <option value="parent">Parent (Progress Tracking)</option>
                                    </optgroup>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <button type="submit" disabled={statusMessage === 'saving' || !email.trim()}
                                className="bg-[#8B1A2D] hover:bg-[#6A1221] text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50">
                                {statusMessage === 'saving' ? 'Granting Access...' : 'Grant Access'}
                            </button>
                        </div>
                    </form>
                )}
                {statusMessage === 'success' && <p className="text-emerald-400 text-xs font-bold mt-4 flex items-center gap-1 justify-end"><CheckCircle size={12} /> Access granted successfully.</p>}
            </div>

            {/* Users Table */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-800/40">
                    <div className="flex gap-4 items-center">
                        <Users size={14} className="text-zinc-500" />
                        <span className="text-zinc-300 font-bold text-xs uppercase tracking-widest">
                            {users.length} Whitelisted Users
                        </span>
                    </div>
                </div>

                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-zinc-800">
                            <th className="p-4 text-xs font-black uppercase tracking-widest text-zinc-500">User Identity</th>
                            <th className="p-4 text-xs font-black uppercase tracking-widest text-zinc-500">Assigned Role</th>
                            <th className="p-4 text-xs font-black uppercase tracking-widest text-zinc-500">Access Granted</th>
                            <th className="p-4 text-xs font-black uppercase tracking-widest text-zinc-500 text-right">Revoke</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {loading ? (
                            <tr><td colSpan="4" className="p-8 text-center text-zinc-500 text-xs">Loading Directory...</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan="4" className="p-8 text-center text-zinc-500 text-xs">No users found in whitelist.</td></tr>
                        ) : users.map(u => (
                            <tr key={u.id} className="hover:bg-zinc-800/50 transition-colors">
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300 font-black text-xs uppercase">
                                            {u.displayName ? u.displayName.charAt(0) : u.id.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm text-white">{u.displayName || u.id}</p>
                                            <p className="font-mono text-[10px] text-zinc-500 mt-1">{u.id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 text-[10px] border font-black uppercase tracking-widest rounded-full ${getRoleBadgeColor(u.role)}`}>
                                        {u.role.replace('_', ' ')}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <p className="text-xs text-zinc-400 font-bold">
                                        {u.addedAt ? new Date(u.addedAt).toLocaleDateString() : 'Unknown'}
                                    </p>
                                    <p className="font-mono text-[10px] text-zinc-500 mt-1 uppercase">Via {u.assignedVia || 'System'}</p>
                                </td>
                                <td className="p-4 text-right">
                                    {u.role !== 'ecosystem_director' && u.id !== user.email && (
                                        <button onClick={() => handleDelete(u.id, u.role)}
                                            className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-900/30 transition-colors"
                                            title="Revoke Access">
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AccessManager;
